/**
 * File upload component with progress tracking and OBS direct upload.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Upload, Progress, Button, message, Space, Typography } from 'antd';
import { UploadOutlined, DeleteOutlined, FileOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import { useFileUpload } from '@/hooks/useFileUpload';
import { formatFileSize } from '@/utils';
import { useT } from '@/i18n';
import type { UploadConfig } from '@/types';

const { Text } = Typography;

interface UploadedFile {
  objectKey: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  md5?: string;  // MD5 hash (optional)
}

interface FileUploadProps {
  fileType: string;
  onUploadComplete: (file: UploadedFile) => void;
  onRemove?: () => void;
  existingFile?: {
    name: string;
    size: number;
    md5?: string;  // Optional MD5 hash
  } | null;
  disabled?: boolean;
  required?: boolean;
  showLabel?: boolean;
  uploadConfig?: UploadConfig;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  fileType,
  onUploadComplete,
  onRemove,
  existingFile,
  disabled = false,
  required = false,
  showLabel = true,
  uploadConfig,
}) => {
  const { t } = useT();
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isRemoved, setIsRemoved] = useState(false);
  const lastUploadedFileRef = useRef<RcFile | null>(null);
  const { isUploading, progress, error, upload, reset } = useFileUpload(uploadConfig);

  useEffect(() => {
    // When existingFile is removed (becomes undefined/null) AND there's no uploaded file,
    // mark as removed so the file disappears from UI
    if (!existingFile && !uploadedFile) {
      setIsRemoved(true);  // ✅ Set to true so currentFile becomes null
      setUploadedFile(null);
      reset();
    }
  }, [existingFile, uploadedFile, reset]);

  const handleUpload = useCallback(
    async (file: RcFile) => {
      lastUploadedFileRef.current = file;

      try {
        const result = await upload(file);
        setUploadedFile(result);
        setIsRemoved(false);
        onUploadComplete(result);
        message.success(t('{name} 上传成功', { name: file.name }));
      } catch {
        message.error(error || t('上传失败'));
      }
      return false; // Prevent default upload behavior
    },
    [upload, onUploadComplete, error, t]
  );

  const handleRemove = useCallback(() => {
    // ✅ Only notify parent component, do NOT clear state immediately
    // Parent will delete uploadedFiles[category] after user confirms in Modal.confirm
    // This will cause existingFile prop to become undefined, triggering re-render
    onRemove?.();

    // ❌ Removed immediate state clearing:
    // setUploadedFile(null);
    // setIsRemoved(true);
    // reset();
  }, [onRemove]);

  const handleRetryUpload = useCallback(async () => {
    if (!lastUploadedFileRef.current || isUploading) return;

    const file = lastUploadedFileRef.current;
    reset(); // 清空错误状态

    try {
      const result = await upload(file);
      setUploadedFile(result);
      setIsRemoved(false);
      onUploadComplete(result);
      message.success(t('{name} 上传成功', { name: file.name }));
    } catch {
      message.error(error || t('上传失败'));
    }
  }, [upload, onUploadComplete, error, reset, isUploading, t]);

  const currentFile = isRemoved ? null : (uploadedFile || existingFile);

  return (
    <div className="file-upload-wrapper" style={{ overflow: 'hidden' }}>
      {showLabel && (
        <div className="flex items-center gap-2 mb-2">
          <Text strong>
            {fileType}
            {required && <span className="text-red-500 ml-1">*</span>}
          </Text>
        </div>
      )}

      {currentFile ? (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded border">
          <Space>
            <FileOutlined className="text-blue-500" />
            <div>
              <Text>{'fileName' in currentFile ? currentFile.fileName : currentFile.name}</Text>
              <br />
              <Space orientation="vertical" size={4} style={{ marginTop: '4px' }}>
                <Text type="secondary" className="text-xs">
                  {formatFileSize('fileSize' in currentFile ? currentFile.fileSize : currentFile.size)}
                </Text>
                {/* Display MD5 hash if available (from either uploaded file or existing file) */}
                {('md5' in currentFile && currentFile.md5) && (
                  <Text
                    type="secondary"
                    copyable={{ text: currentFile.md5 }}
                    title={currentFile.md5}
                    className="text-xs"
                  >
                    MD5: {currentFile.md5}
                  </Text>
                )}
              </Space>
            </div>
          </Space>
          {!disabled && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
            >
              {t('删除')}
            </Button>
          )}
        </div>
      ) : (
        <Upload.Dragger
          name="file"
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={disabled || isUploading}
          className="upload-dragger"
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">{t('点击或拖拽文件到此区域上传')}</p>
          <p className="ant-upload-hint">{t('支持单个文件上传')}</p>
        </Upload.Dragger>
      )}

      {isUploading && progress && (
        <div className="mt-2">
          <Progress
            percent={progress.percent}
            size="small"
            status={
              progress.status === 'merging' ? 'active' :
              progress.percent === 100 ? 'success' :
              'active'
            }
            className="mb-1"
            format={() => `${formatFileSize(progress.loaded)} / ${formatFileSize(progress.total)}`}
          />

          {/* Merging phase hint */}
          {progress.status === 'merging' && (
            <Text type="secondary" className="text-xs">
              {t('合并中，请稍候...')}
            </Text>
          )}
        </div>
      )}

      {error && (
        <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded">
          <div className="flex justify-between items-start gap-3">
            <Text type="danger" className="whitespace-pre-wrap break-words text-sm flex-1">
              {error}
            </Text>
            <Button
              type="primary"
              size="small"
              onClick={handleRetryUpload}
              disabled={isUploading}
              className="flex-shrink-0"
            >
              {t('重新上传')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
